import React, { useCallback, useEffect, useState } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import { detailToString } from "../utils/apiError";

const API_BASE = config.API_BASE_URL;
const pad6 = (n) => String(n).padStart(6, "0");

/** 'drop' | 'remnant' (an API from before 2026-10-15 sends no kind: remnants only). */
export const pieceKind = (r) => (r && r.kind === "drop" ? "drop" : "remnant");

/** "3 traced · 1 partly traced · 2 not traced" for one kind's status counts. */
function statusLine(c = {}) {
  const old = c.traced_legacy ? ` · ${c.traced_legacy} traced (old tags)` : "";
  return `${c.traced || 0} traced${old} · ${c.partial || 0} partly traced · ${c.untraced || 0} not traced`;
}

/**
 * Remnant origins (2026-10-14). Mike: "As long as we can accurately trace the origin of the remnants
 * and their heat number, that's what we're most concerned with."
 *
 * Every remnant in stock with pieces no lot accounts for (GET /reports/remnant_lineage: status
 * untraced / partial): pieces cut before remnants had lots of their own, saw tails before the saw
 * recorded its stick, sheets cut with no TRK. Someone reads the tag on the floor and records it
 * here: POST /materials/{id}/remnant_lots {mtn_id, pieces, pin, note} (Admin / Manager PIN, at most
 * the unrecorded pieces, a lot of the material it was cut from). The pieces then join the remnant's
 * own lot from that lot, with its heat.
 *
 * Since 2026-10-15 the report lists STOCK DROPS too (Mike: "add drops to the Remnant origins report"):
 * 8x48 strips, 19x29 blanks, saw cut-to-length bars, credited by cutting a sheet / stick with no lot until
 * then. Rows carry `kind` ('remnant' | 'drop'); a drop's "cut from" is the sheet / stick material. A drop's
 * tag (printed since 2026-04-30) shows the TRK of the sheet it was cut from; recording it is the same POST.
 */
export default function RemnantLineageModal({ show, onHide, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);       // material_id being recorded
  const [form, setForm] = useState({ mtn: "", pieces: "1", note: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {ok, text}
  const [kindFilter, setKindFilter] = useState("all");   // all | remnant | drop

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/reports/remnant_lineage?kind=all`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(`Could not load remnant origins: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (show) load(); }, [show, load]);

  if (!show) return null;

  const toRead = (data?.remnants || []).filter((r) => r.unrecorded_pieces > 0);
  const nToRead = { remnant: 0, drop: 0 };
  toRead.forEach((r) => { nToRead[pieceKind(r)] += 1; });
  const rows = kindFilter === "all" ? toRead : toRead.filter((r) => pieceKind(r) === kindFilter);
  const byKind = data?.counts_by_kind || (data ? { remnant: data.counts || {} } : {});
  const piecesByKind = data?.unrecorded_by_kind || (data ? { remnant: data.unrecorded_pieces || 0 } : {});
  const hasDrops = !!byKind.drop;

  function startRecord(r) {
    setOpen(r.material_id);
    setResult(null);
    setForm({ mtn: r.candidate_lots?.length === 1 ? String(r.candidate_lots[0].mtn_id) : "", pieces: "1", note: "" });
  }

  async function record(r) {
    const mtn = parseInt(String(form.mtn).replace(/\D/g, ""), 10);
    const pieces = parseInt(form.pieces, 10);
    if (!mtn) { setResult({ ok: false, text: "Enter the TRK # from the tag." }); return; }
    if (!pieces || pieces < 1 || pieces > r.unrecorded_pieces) {
      setResult({ ok: false, text: `Pieces must be 1 to ${r.unrecorded_pieces}.` });
      return;
    }
    if (form.note.trim().length < 5) { setResult({ ok: false, text: "Say who read which tag (at least 5 characters)." }); return; }
    const pin = await pinPrompt("Manager PIN to record the tag read:", { autofill: false });
    if (!pin) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/materials/${r.material_id}/remnant_lots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mtn_id: mtn, pieces, pin, note: form.note.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult({ ok: false, text: detailToString(body.detail) || `HTTP ${res.status}` });
      } else {
        setResult({ ok: true, text: `Recorded: ${pieces} piece${pieces === 1 ? "" : "s"} of ${r.material_code} now on TRK ${pad6(body.mtn_id)} (from TRK ${pad6(mtn)}).` });
        setOpen(null);
        await load();
        if (onChanged) onChanged();
      }
    } catch (e) {
      setResult({ ok: false, text: `Not recorded: ${e.message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">Remnant origins</h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={busy}></button>
          </div>
          <div className="modal-body">
            <p className="small text-muted mb-2">
              Remnants and stock drops in stock whose pieces have no lot on record. Read the TRK on each piece's tag
              (a drop's tag shows the TRK of the sheet or bar it was cut from) and record it: the pieces join the
              piece's own lot from that TRK, with its heat.
            </p>
            {data && (
              <div className="small mb-2" data-testid="remnant-counts">
                <div>
                  <strong>Remnants:</strong> {statusLine(byKind.remnant)} ({piecesByKind.remnant || 0} pieces to read)
                </div>
                {hasDrops && (
                  <div data-testid="drop-counts">
                    <strong>Stock drops:</strong> {statusLine(byKind.drop)} ({piecesByKind.drop || 0} pieces to read)
                  </div>
                )}
              </div>
            )}
            {data && hasDrops && (
              <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Show">
                {[["all", `All (${toRead.length})`], ["remnant", `Remnants (${nToRead.remnant})`],
                  ["drop", `Stock drops (${nToRead.drop})`]].map(([k, label]) => (
                  <button key={k} type="button"
                          className={`btn ${kindFilter === k ? "btn-primary" : "btn-outline-primary"}`}
                          aria-pressed={kindFilter === k} onClick={() => { setKindFilter(k); setOpen(null); }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {loading && <div className="text-muted">Loading…</div>}
            {error && <div className="alert alert-danger">{error}</div>}
            {result && <div className={`alert ${result.ok ? "alert-success" : "alert-warning"} py-2`}>{result.text}</div>}
            {data && !loading && rows.length === 0 && (
              <div className="alert alert-success">
                {kindFilter === "drop" ? "Every stock drop in stock traces to a lot."
                  : kindFilter === "remnant" ? "Every remnant in stock traces to a lot."
                  : hasDrops ? "Every remnant and stock drop in stock traces to a lot."
                  : "Every remnant in stock traces to a lot."}
              </div>
            )}
            {rows.length > 0 && (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>{hasDrops ? "Remnant / drop" : "Remnant"}</th><th>Cut from</th><th className="text-end">In stock</th>
                    <th className="text-end">No lot</th><th>Lots on record</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <React.Fragment key={r.material_id}>
                      <tr>
                        <td>
                          <div className="fw-bold">
                            {r.material_code}
                            {pieceKind(r) === "drop" && (
                              <span className="badge bg-info text-dark ms-2" data-testid={`drop-badge-${r.material_id}`}>Stock drop</span>
                            )}
                          </div>
                          <div className="small text-muted">
                            {r.owner_name || ""}{r.created_at ? ` · made ${new Date(r.created_at).toLocaleDateString()}` : ""}
                          </div>
                        </td>
                        <td className="small">{r.source_material_code || "—"}</td>
                        <td className="text-end">{r.stock_quantity}</td>
                        <td className="text-end fw-bold text-danger">{r.unrecorded_pieces}</td>
                        <td className="small">
                          {(r.lots || []).length
                            ? r.lots.map((l) => `TRK ${pad6(l.mtn_id)} (${l.pieces}${l.heat_number ? `, heat ${l.heat_number}` : ""})`).join(", ")
                            : "—"}
                        </td>
                        <td className="text-end">
                          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy}
                                  onClick={() => (open === r.material_id ? setOpen(null) : startRecord(r))}>
                            {open === r.material_id ? "Close" : "Record tag…"}
                          </button>
                        </td>
                      </tr>
                      {open === r.material_id && (
                        <tr className="table-light">
                          <td colSpan={6}>
                            <div className="row g-2 align-items-end">
                              <div className="col-md-5">
                                <label className="form-label small mb-1">TRK on the tag</label>
                                <input className="form-control form-control-sm" list={`lots-${r.material_id}`}
                                       aria-label="TRK on the tag" value={form.mtn} placeholder="e.g. 000451"
                                       onChange={(e) => setForm((f) => ({ ...f, mtn: e.target.value }))} />
                                <datalist id={`lots-${r.material_id}`}>
                                  {(r.candidate_lots || []).map((c) => (
                                    <option key={c.mtn_id} value={pad6(c.mtn_id)}>
                                      {`${c.material_code}${c.heat_number ? ` · heat ${c.heat_number}` : ""}${c.po_number ? ` · PO ${c.po_number}` : ""}`}
                                    </option>
                                  ))}
                                </datalist>
                                <div className="small text-muted mt-1">
                                  Lots it could be from: {(r.candidate_lots || []).slice(0, 6).map((c) => pad6(c.mtn_id)).join(", ") || "none on record"}
                                </div>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Pieces</label>
                                <input className="form-control form-control-sm" inputMode="numeric" aria-label="Pieces"
                                       value={form.pieces} onChange={(e) => setForm((f) => ({ ...f, pieces: e.target.value.replace(/\D/g, "") }))} />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small mb-1">Note (who read it, where)</label>
                                <input className="form-control form-control-sm" aria-label="Note"
                                       value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                              </div>
                              <div className="col-md-2">
                                <button type="button" className="btn btn-sm btn-primary w-100" disabled={busy}
                                        onClick={() => record(r)}>
                                  {busy ? "Saving…" : "Record"}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={load} disabled={loading || busy}>Refresh</button>
            <button type="button" className="btn btn-secondary" onClick={onHide} disabled={busy}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
